import {
  forwardRef,
  useState,
  useRef,
  useCallback,
  useEffect,
  cloneElement,
  isValidElement,
} from 'react';
import type { CSSProperties, HTMLAttributes } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import type { TooltipProps, TooltipPosition } from './Tooltip.types';

/**
 * Custom hook to detect if an element's content is overflowing
 */
function useOverflowDetection(enabled: boolean) {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const elementRef = useRef<HTMLElement | null>(null);

  const checkOverflow = useCallback(() => {
    if (!enabled || !elementRef.current) {
      setIsOverflowing(false);
      return;
    }
    const el = elementRef.current;
    const hasOverflow = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
    setIsOverflowing(hasOverflow);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    
    checkOverflow();

    // Use ResizeObserver if available
    if (typeof ResizeObserver !== 'undefined' && elementRef.current) {
      const observer = new ResizeObserver(checkOverflow);
      observer.observe(elementRef.current);
      return () => observer.disconnect();
    }

    // Fallback to window resize
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [enabled, checkOverflow]);

  const setRef = useCallback((node: HTMLElement | null) => {
    elementRef.current = node;
    if (node && enabled) {
      checkOverflow();
    }
  }, [enabled, checkOverflow]);

  return { isOverflowing, setRef, checkOverflow };
}

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.95);
  }
`;

// Styled components
const TooltipContainer = styled.div<{
  $position: TooltipPosition;
  $maxWidth: number | string;
  $zIndex: number;
  $isVisible: boolean;
  $isLeaving: boolean;
}>`
  position: fixed;
  z-index: ${({ $zIndex }) => $zIndex};
  max-width: ${({ $maxWidth }) =>
    typeof $maxWidth === 'number' ? `${$maxWidth}px` : $maxWidth};
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.4;
  font-family: var(--ib-font-family, 'Roboto', sans-serif);
  color: var(--ib-color-grey-900, #191919);
  background-color: var(--ib-color-grey-100, #F7F7F7);
  border-radius: 4px;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.25);
  word-wrap: break-word;
  overflow-wrap: break-word;
  pointer-events: none;
  
  animation: ${({ $isLeaving }) => ($isLeaving ? fadeOut : fadeIn)} 0.15s ease-out;
  animation-fill-mode: forwards;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: ${({ $isLeaving }) => ($isLeaving ? 0 : 1)};
  }
`;

const Arrow = styled.div<{ $position: TooltipPosition }>`
  position: absolute;
  width: 8px;
  height: 8px;
  background-color: var(--ib-color-grey-100, #F7F7F7);
  transform: rotate(45deg);

  ${({ $position }) => {
    if ($position.startsWith('top')) {
      return `
        bottom: -4px;
        box-shadow: 2px 2px 3px rgba(0, 0, 0, 0.1);
      `;
    }
    if ($position.startsWith('bottom')) {
      return `
        top: -4px;
        box-shadow: -2px -2px 3px rgba(0, 0, 0, 0.1);
      `;
    }
    if ($position.startsWith('left')) {
      return `
        right: -4px;
        box-shadow: 2px -2px 3px rgba(0, 0, 0, 0.1);
      `;
    }
    if ($position.startsWith('right')) {
      return `
        left: -4px;
        box-shadow: -2px 2px 3px rgba(0, 0, 0, 0.1);
      `;
    }
    return '';
  }}

  ${({ $position }) => {
    if ($position.endsWith('-start')) {
      return $position.startsWith('top') || $position.startsWith('bottom')
        ? 'left: 12px;'
        : 'top: 12px;';
    }
    if ($position.endsWith('-end')) {
      return $position.startsWith('top') || $position.startsWith('bottom')
        ? 'right: 12px;'
        : 'bottom: 12px;';
    }
    // Center alignment
    if ($position === 'top' || $position === 'bottom') {
      return 'left: 50%; margin-left: -4px;';
    }
    return 'top: 50%; margin-top: -4px;';
  }}
`;

const TriggerWrapper = styled.span`
  display: inline-flex;
`;

// Position calculation utilities
function getPosition(
  triggerRect: DOMRect,
  tooltipRect: { width: number; height: number },
  position: TooltipPosition,
  offset: number
): { top: number; left: number } {
  const scrollY = window.scrollY || window.pageYOffset;
  const scrollX = window.scrollX || window.pageXOffset;

  let top = 0;
  let left = 0;

  // Vertical positioning
  if (position.startsWith('top')) {
    top = triggerRect.top + scrollY - tooltipRect.height - offset;
  } else if (position.startsWith('bottom')) {
    top = triggerRect.bottom + scrollY + offset;
  } else if (position.startsWith('left') || position.startsWith('right')) {
    if (position.endsWith('-start')) {
      top = triggerRect.top + scrollY;
    } else if (position.endsWith('-end')) {
      top = triggerRect.bottom + scrollY - tooltipRect.height;
    } else {
      top = triggerRect.top + scrollY + (triggerRect.height - tooltipRect.height) / 2;
    }
  }

  // Horizontal positioning
  if (position.startsWith('left')) {
    left = triggerRect.left + scrollX - tooltipRect.width - offset;
  } else if (position.startsWith('right')) {
    left = triggerRect.right + scrollX + offset;
  } else if (position.startsWith('top') || position.startsWith('bottom')) {
    if (position.endsWith('-start')) {
      left = triggerRect.left + scrollX;
    } else if (position.endsWith('-end')) {
      left = triggerRect.right + scrollX - tooltipRect.width;
    } else {
      left = triggerRect.left + scrollX + (triggerRect.width - tooltipRect.width) / 2;
    }
  }

  return { top, left };
}

/**
 * Tooltip component - Displays helpful text on hover/focus
 *
 * @example
 * ```tsx
 * // Basic usage
 * <Tooltip content="Helpful text">
 *   <Button>Hover me</Button>
 * </Tooltip>
 *
 * // Different positions
 * <Tooltip content="Top tooltip" position="top">
 *   <span>Top</span>
 * </Tooltip>
 *
 * // With delay
 * <Tooltip content="Delayed tooltip" delay={500}>
 *   <Button>Wait for it</Button>
 * </Tooltip>
 *
 * // Disabled
 * <Tooltip content="Won't show" disabled>
 *   <span>No tooltip</span>
 * </Tooltip>
 * ```
 */
export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      content,
      children,
      position = 'bottom',
      delay = 200,
      hideDelay = 0,
      disabled = false,
      maxWidth = 300,
      contentStyle,
      contentClassName,
      arrow = false,
      zIndex = 1000,
      offset = 8,
      open: controlledOpen,
      onOpenChange,
      onlyWhenOverflow = false,
      'data-testid': testId,
      dataTestId,
      dataId,
      ...rest
    },
    ref
  ) => {
    // Overflow detection for onlyWhenOverflow mode
    const { isOverflowing, setRef: setOverflowRef } = useOverflowDetection(onlyWhenOverflow);
    const isControlled = controlledOpen !== undefined;
    const [internalOpen, setInternalOpen] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number }>({
      top: 0,
      left: 0,
    });

    const triggerRef = useRef<HTMLElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isOpen = isControlled ? controlledOpen : internalOpen;

    const setOpen = useCallback(
      (value: boolean) => {
        if (!isControlled) {
          setInternalOpen(value);
        }
        onOpenChange?.(value);
      },
      [isControlled, onOpenChange]
    );

    const updatePosition = useCallback(() => {
      if (!triggerRef.current || !tooltipRef.current) return;

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = {
        width: tooltipRef.current.offsetWidth,
        height: tooltipRef.current.offsetHeight,
      };

      const pos = getPosition(triggerRect, tooltipRect, position, offset);
      setTooltipPosition(pos);
    }, [position, offset]);

    const handleShow = useCallback(() => {
      // Check effective disabled state including overflow mode
      const isEffectivelyDisabled = disabled || (onlyWhenOverflow && !isOverflowing);
      if (isEffectivelyDisabled) return;

      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
      setIsLeaving(false);

      showTimeoutRef.current = setTimeout(() => {
        setOpen(true);
      }, delay);
    }, [delay, disabled, onlyWhenOverflow, isOverflowing, setOpen]);

    const handleHide = useCallback(() => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);

      hideTimeoutRef.current = setTimeout(() => {
        setIsLeaving(true);
        leaveTimeoutRef.current = setTimeout(() => {
          setOpen(false);
          setIsLeaving(false);
        }, 150);
      }, hideDelay);
    }, [hideDelay, setOpen]);

    // Update position when tooltip becomes visible
    useEffect(() => {
      if (isOpen && !isLeaving) {
        // Wait for next frame to ensure tooltip is rendered
        requestAnimationFrame(() => {
          updatePosition();
        });
      }
    }, [isOpen, isLeaving, updatePosition]);

    // Cleanup timeouts on unmount
    useEffect(() => {
      return () => {
        if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
        if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
      };
    }, []);

    // Close on scroll or resize
    useEffect(() => {
      if (!isOpen) return;

      const handleScrollOrResize = () => {
        handleHide();
      };

      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);

      return () => {
        window.removeEventListener('scroll', handleScrollOrResize, true);
        window.removeEventListener('resize', handleScrollOrResize);
      };
    }, [isOpen, handleHide]);

    // If no content or disabled (but not in overflow mode), just render children
    if (!content || (disabled && !onlyWhenOverflow)) {
      return <>{children}</>;
    }

    // Combined ref setter that handles both triggerRef and overflow detection ref
    const setCombinedRef = (node: HTMLElement | null) => {
      (triggerRef as React.MutableRefObject<HTMLElement | null>).current = node;
      if (onlyWhenOverflow) {
        setOverflowRef(node);
      }
    };

    // Type for child element props
    type ChildProps = HTMLAttributes<HTMLElement> & {
      ref?: React.Ref<HTMLElement>;
    };

    // Clone child to add event handlers
    const trigger = isValidElement<ChildProps>(children) ? (
      cloneElement(children, {
        ref: setCombinedRef,
        onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
          handleShow();
          children.props.onMouseEnter?.(e);
        },
        onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
          handleHide();
          children.props.onMouseLeave?.(e);
        },
        onFocus: (e: React.FocusEvent<HTMLElement>) => {
          handleShow();
          children.props.onFocus?.(e);
        },
        onBlur: (e: React.FocusEvent<HTMLElement>) => {
          handleHide();
          children.props.onBlur?.(e);
        },
      } as ChildProps)
    ) : (
      <TriggerWrapper
        ref={(node) => setCombinedRef(node as HTMLElement | null)}
        onMouseEnter={handleShow}
        onMouseLeave={handleHide}
        onFocus={handleShow}
        onBlur={handleHide}
        tabIndex={0}
      >
        {children}
      </TriggerWrapper>
    );

    const tooltipStyles: CSSProperties = {
      ...contentStyle,
      top: tooltipPosition.top,
      left: tooltipPosition.left,
    };

    return (
      <>
        {trigger}
        {(isOpen || isLeaving) &&
          createPortal(
            <TooltipContainer
              ref={(node) => {
                (tooltipRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
                if (typeof ref === 'function') {
                  ref(node);
                } else if (ref) {
                  ref.current = node;
                }
              }}
              role="tooltip"
              style={tooltipStyles}
              className={contentClassName}
              $position={position}
              $maxWidth={maxWidth}
              $zIndex={zIndex}
              $isVisible={isOpen && !isLeaving}
              $isLeaving={isLeaving}
              data-testid={dataTestId || testId}
              data-id={dataId}
              {...rest}
            >
              {content}
              {arrow && <Arrow $position={position} />}
            </TooltipContainer>,
            document.body
          )}
      </>
    );
  }
);

Tooltip.displayName = 'Tooltip';

export default Tooltip;
